import { Box, Flex, Grid, useToast } from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { getFromUrl, serverRequest } from '../../api/Api';
import Pagination from '../../components/Pagination';
import PopoverMenu from '../../components/Popover';
import Referrals from '../../components/Resources/Referrals';
import ResourcesItems from '../../components/Resources/ResourcesItems';
import { colors } from '../../theme/colors';
import { Icon } from '../Icons';
import { Body } from '../Texts/Texts';

const FolderResourcesSection = () => {
  const toast = useToast();
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [folder, setFolder] = useState<any>([]);
  const [pagination, setPagination] = useState<any>(null);
  const fileInput = React.useRef<null | HTMLInputElement>(null);

  const currentPage = parseInt(searchParams.get('page') || '1', 10);

  const handlePageChange = (page: number | string) => {
    if (typeof page === 'number') {
      setSearchParams({ page: page.toString() });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchFiles();
  }, [currentPage]);

  const fetchFiles = () => {
    // Backend uses 0-based indexing, UI uses 1-based
    const backendPage = currentPage - 1;
    return getFromUrl(`resources/${id}/all/files?page=${backendPage}`).then(
      (res) => {
        setFolder(res);
        setPagination(res?.pagination || null);
        setLoading(false);
      }
    );
  };

  const selectFile = () => {
    fileInput.current?.click();
  };

  const onChangeFile = (event: any) => {
    event.preventDefault();
    console.log('Starting upload...');
    setUploading(true);
    console.log('Uploading state set to true');
    const files = event.target.files;
    const formData = new FormData();
    for (const file of files) {
      formData.append('files', file);
    }
    serverRequest({
      endpoint: `resources/${id}/files`,
      body: formData,
      method: 'post',
      type: 'formData'
    })
      .then((res) => {
        if (res?.results) {
          fetchFiles();
          toast({
            position: 'top',
            title: 'Success',
            description: `Files upload successful`,
            status: 'success',
            duration: 3000,
            isClosable: true
          });
        } else {
          toast({
            position: 'top',
            title: 'Error',
            description: 'Error uploading files',
            status: 'error',
            duration: 3000,
            isClosable: true
          });
        }
      })
      .catch((error) => {
        toast({
          position: 'top',
          title: 'Error',
          description: 'Error uploading files',
          status: 'error',
          duration: 3000,
          isClosable: true
        });
      })
      .finally(() => {
        console.log('Upload completed, setting uploading to false');
        setUploading(false);
      });
  };

  console.log('Rendering - uploading:', uploading, 'loading:', loading);

  return (
    <>
      <style>{`
        @keyframes folderUploadSpin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      <Grid
        gridTemplateColumns='2.4fr 1fr'
        gap='56px'
        mt='32px'
        pb='64px'
        pr='60px'
      >
        <Box>
          <PopoverMenu
            trigger={
              <Flex
                cursor={uploading ? 'not-allowed' : 'pointer'}
                alignItems='center'
                justifyContent='center'
                color='deep_blue'
                mr='26px'
                bg='accent'
                w='244px'
                h='55px'
                borderRadius='12px'
                mb='36px'
                px='20px'
                opacity={uploading ? 0.6 : 1}
                pointerEvents={uploading ? 'none' : 'auto'}
              >
                {uploading ? (
                  <div
                    style={{
                      width: '24px',
                      height: '24px',
                      border: '3px solid rgba(255, 255, 255, 0.3)',
                      borderTop: '3px solid white',
                      borderRadius: '50%',
                      animation: 'folderUploadSpin 0.8s linear infinite',
                      marginRight: '26px'
                    }}
                  />
                ) : (
                  <>
                    <Body
                      color='white'
                      textTransform='uppercase'
                      size='14px'
                      mr='26px'
                      fontWeight={500}
                    >
                      Add new
                    </Body>
                    <Icon name='Upload' color={colors.white} size={20} filled />
                  </>
                )}
              </Flex>
            }
            top='130px'
            w='334px'
            placement='top-start'
          >
            {(onClose: any) => (
              <Box fontSize='19px' fontWeight={500} color='gray'>
                <Flex
                  onClick={() => {
                    if (!uploading) {
                      onClose();
                      selectFile();
                    }
                  }}
                  py='14px'
                  justifyContent='center'
                  cursor={uploading ? 'not-allowed' : 'pointer'}
                  opacity={uploading ? 0.5 : 1}
                  pointerEvents={uploading ? 'none' : 'auto'}
                >
                  {uploading ? 'Uploading...' : 'Upload File'}
                </Flex>
              </Box>
            )}
          </PopoverMenu>
          {!loading && !uploading ? (
            <>
              <ResourcesItems
                type='files'
                data={folder?.data?.files}
                view='folderView'
                folderName={folder?.data?.folder?.folderName}
              />
              {pagination && pagination.totalPages > 1 && (
                <Pagination
                  currentPage={currentPage}
                  totalCount={pagination.totalItems}
                  pageSize={pagination.pageSize}
                  onPageChange={handlePageChange}
                />
              )}
            </>
          ) : (
            <Flex justifyContent='center' alignItems='center' minH='200px'>
              <div
                style={{
                  width: '48px',
                  height: '48px',
                  border: '4px solid rgba(0, 0, 0, 0.1)',
                  borderTop: '4px solid #3B82F6',
                  borderRadius: '50%',
                  animation: 'folderUploadSpin 0.8s linear infinite'
                }}
              />
            </Flex>
          )}

          <input
            multiple={true}
            type='file'
            id='file'
            ref={fileInput}
            style={{ display: 'none' }}
            onChange={onChangeFile}
          />
        </Box>
        <Referrals />
      </Grid>
    </>
  );
};

export default FolderResourcesSection;
